import { useEffect, useRef, useState } from "react";
import { Plus, FileText, Trash2, LogOut, PanelLeftClose, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NOTEPAD } from "@/constants/testIds";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Sidebar({ notes, activeId, onSelect, onCreate, onDelete, onRename, onLogout, onCollapse }) {
  const [renamingId, setRenamingId] = useState(null);
  const [tempName, setTempName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (renamingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renamingId]);

  const startRename = (note) => {
    setRenamingId(note.id);
    setTempName(note.title || "");
  };

  const commitRename = () => {
    if (renamingId && tempName.trim()) {
      onRename(renamingId, tempName.trim());
    }
    setRenamingId(null);
  };
  return (
    <aside
      data-testid={NOTEPAD.sidebar}
      className="h-full bg-[#0A0B10] border-r border-[#2A2F45] flex flex-col"
    >
      {/* Header */}
      <div className="px-4 py-4 border-b border-[#2A2F45] flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-400">Notepad+</div>
          <div className="text-sm font-semibold text-white">Catatan Saya</div>
        </div>
        <button
          onClick={onCollapse}
          data-testid={NOTEPAD.sidebarToggle}
          className="text-[#8B949E] hover:text-white transition-colors p-1"
          title="Sembunyikan"
        >
          <PanelLeftClose className="w-4 h-4" />
        </button>
      </div>

      {/* New note */}
      <div className="p-3">
        <Button
          onClick={onCreate}
          data-testid={NOTEPAD.newNoteBtn}
          className="w-full justify-start gap-2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:border-indigo-500/50 transition-all"
          variant="ghost"
        >
          <Plus className="w-4 h-4" />
          Catatan Baru
        </Button>
      </div>

      {/* Notes list */}
      <div className="flex-1 overflow-y-auto px-2">
        {notes.length === 0 && (
          <div className="px-3 py-8 text-center text-xs text-[#4B5563]">
            Belum ada catatan.
            <br />Klik &quot;Catatan Baru&quot; untuk memulai.
          </div>
        )}
        {notes.map((note) => {
          const isRenaming = renamingId === note.id;
          return (
          <div
            key={note.id}
            data-testid={NOTEPAD.sidebarNoteItem(note.id)}
            onClick={() => !isRenaming && onSelect(note.id)}
            onDoubleClick={() => startRename(note)}
            className={`group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer mb-1 transition-colors ${
              activeId === note.id
                ? "bg-indigo-500/15 text-white"
                : "text-[#8B949E] hover:bg-[#1C1F2E] hover:text-white"
            }`}
          >
            <FileText className="w-4 h-4 flex-shrink-0" />

            {isRenaming ? (
              <input
                ref={inputRef}
                data-testid={`sidebar-rename-input-${note.id}`}
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setRenamingId(null);
                }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0A0B10] border border-indigo-500/50 text-white text-sm px-2 py-0.5 rounded outline-none flex-1"
              />
            ) : (
              <span className="text-sm truncate flex-1">{note.title || "Tanpa Judul"}</span>
            )}

            {!isRenaming && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startRename(note);
                }}
                data-testid={`sidebar-rename-${note.id}`}
                title="Ubah nama catatan"
                className="opacity-0 group-hover:opacity-100 text-[#4B5563] hover:text-indigo-400 transition-all"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid={NOTEPAD.sidebarDeleteBtn(note.id)}
                  onClick={(e) => e.stopPropagation()}
                  title="Hapus catatan"
                  className="opacity-0 group-hover:opacity-100 text-[#4B5563] hover:text-red-400 transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#12141D] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Hapus catatan?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#8B949E]">
                    Catatan &quot;{note.title}&quot; akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">
                    Batal
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(note.id)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    Hapus
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-[#2A2F45]">
        <button
          onClick={onLogout}
          data-testid={NOTEPAD.logoutBtn}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8B949E] hover:text-white hover:bg-[#1C1F2E] rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Keluar
        </button>
      </div>
    </aside>
  );
}
